<template>
  <div v-if="customer" id="profile-container" class="wdgt-container" ref="container">
    <VeeForm @submit="update">
      <div class="wdgt-form">
        <div class="form-field">
          <label for="wdgt-profile-name">{{ $t('words.name') }}*</label>
          <VeeField id="wdgt-profile-name" name="contactName" :modelValue="customer.contactName" @update:modelValue="customer.contactName = $event" type="text" :placeholder="$t('words.name')" rules="required" />
          <ErrorMessage name="contactName" class="error" />
        </div>
        <div class="form-field">
          <label for="wdgt-profile-surname">{{ $t('words.surname') }}*</label>
          <VeeField id="wdgt-profile-surname" name="contactSurname" :modelValue="customer.contactSurname" @update:modelValue="customer.contactSurname = $event" type="text" :placeholder="$t('words.surname')" rules="required" />
          <ErrorMessage name="contactSurname" class="error" />
        </div>
        <div class="form-field">
          <label for="wdgt-profile-email">{{ $t('words.email') }}*</label>
          <VeeField id="wdgt-profile-email" name="contactEmail" :modelValue="customer.contactEmail" @update:modelValue="customer.contactEmail = $event" type="email" :placeholder="$t('words.email')" rules="required|email" />
          <ErrorMessage name="contactEmail" class="error" />
        </div>
        <div class="form-field">
          <label for="wdgt-profile-phone">{{ $t('words.phone') }}*</label>
          <PhoneSelector
            v-model="customer.contactPhone1"
            field-id="wdgt-profile-phone"
            field-name="contactPhone1"
            :placeholder="$t('words.phone')"
            rules="required|phone"
          />
        </div>
        <div class="form-field">
          <label for="wdgt-profile-vat">{{ $t('words.vat') }}</label>
          <VeeField id="wdgt-profile-vat" name="vat" :modelValue="customer.vat" @update:modelValue="customer.vat = $event" type="text" :placeholder="$t('words.vat')" rules="validateCIFNIFNIE" />
          <ErrorMessage name="vat" class="error" />
        </div>
        <div class="form-field">
          <label for="wdgt-profile-company">{{ $t('words.company') }} ({{ $t('words.optional') }})</label>
          <input id="wdgt-profile-company" v-model="customer.companyName" type="text" :placeholder="$t('words.company')" />
        </div>
      </div>

      <!-- Sección de Estadísticas -->
      <div class="wdgt-form">
        <div class="form-section-title">
          <h3>{{ $t('words.statistics') }}</h3>
        </div>

        <div class="form-field">
          <label for="wdgt-profile-stats-country">{{ $t('words.statsCountryCode') }}</label>
          <div class="country-selector">
            <input
              id="wdgt-profile-stats-country"
              type="text"
              :placeholder="$t('words.searchCountry')"
              @focus="showCountryDropdown = true"
              @blur="hideCountryDropdown"
              :value="selectedCountryName"
              readonly
            />
            <div v-if="showCountryDropdown" class="country-dropdown">
              <input
                type="text"
                v-model="countrySearch"
                :placeholder="$t('words.searchCountry')"
                class="country-search"
                @focus="keepCountryDropdownOpen"
                @blur="hideCountryDropdownDelayed"
                @click.stop
                ref="countrySearchInput"
              />
              <div class="country-list">
                <div
                  v-for="country in filteredCountries"
                  :key="country.code"
                  @click="selectCountry(country)"
                  class="country-option"
                >
                  {{ country.name }} ({{ country.code }})
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-field">
          <label for="wdgt-profile-stats-postal">{{ $t('words.statsPostalCode') }}</label>
          <input id="wdgt-profile-stats-postal" v-model="customer.statsPostalCode" type="text" :placeholder="$t('words.statsPostalCode')" />
        </div>

        <div class="form-field">
          <label for="wdgt-profile-stats-how-met">{{ $t('words.statsHowMet') }}</label>
          <div class="how-met-selector">
            <input
              id="wdgt-profile-stats-how-met"
              type="text"
              :placeholder="$t('words.statsHowMet')"
              @focus="showHowMetDropdown = true"
              @blur="hideHowMetDropdown"
              :value="selectedHowMetName"
              readonly
            />
            <div v-if="showHowMetDropdown" class="how-met-dropdown">
              <div class="how-met-list">
                <div
                  v-for="option in howMetOptions"
                  :key="option.value"
                  @click="selectHowMet(option.value, option.text)"
                  class="how-met-option"
                >
                  {{ option.text }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="customer.statsHowMet === 'other'" class="form-field">
          <label for="wdgt-profile-stats-how-met-other">{{ $t('words.statsHowMetOther') }}</label>
          <textarea
            id="wdgt-profile-stats-how-met-other"
            v-model="customer.statsHowMetOther"
            :placeholder="$t('words.statsHowMetOther')"
            rows="3"
          />
        </div>
      </div>

      <div class="checkout-buttons">
        <button type="submit" class="updateButton green d-flex ml-auto" :disabled="disableSaveButton">
          {{ $t('actions.send') }}
        </button>
      </div>
    </VeeForm>
  </div>
</template>
<script>
import {mapActions, mapGetters} from 'vuex';
import CustomerRepository from "@/api/CustomerRepository";
import { defineRule, Form, Field, ErrorMessage, configure } from "vee-validate";
import { required, email } from "@vee-validate/rules";
import {i18n} from "@/lang";
import Swal from "sweetalert2";
import countries from "@/utils/countries";
import PhoneSelector from "@/components/common/PhoneSelector.vue";
import { validatePhoneNumber, getPhoneValidationMessage } from "@/utils/phoneValidation";
import { getCountryFromGeolocation } from "@/utils/countryDetection";

defineRule('required', required);
defineRule('email', email);

defineRule('phone', (value) => {
  if (!value || !value.length) {
    return true;
  }
  if (!validatePhoneNumber(value)) {
    return getPhoneValidationMessage(i18n, value);
  }
  return true;
});

defineRule('validateCIFNIFNIE', (value) => {
  if (!value || !value.length) {
    return true;
  }
  if (value.match(/^[0-9A-Za-z]{9,12}$/)) {
    return true;
  }
  return i18n.global.t('forms.dninifcif');
});

configure({
  generateMessage: (ctx) => {
    const messages = {
      required: i18n.global.t('forms.required'),
      email: i18n.global.t('forms.email'),
    };
    return messages[ctx.rule.name] || `The field ${ctx.field} is invalid`;
  },
});

export default {
  components: {
    VeeForm: Form,
    VeeField: Field,
    ErrorMessage,
    PhoneSelector
  },
  data() {
    return {
      placeholderPassword: '*********',
      disableSaveButton: false,
      // Campos para estadísticas
      countries: countries,
      countrySearch: '',
      showCountryDropdown: false,
      selectedCountryName: '',
      showHowMetDropdown: false,
      selectedHowMetName: '',
    }
  },
  computed: {
    ...mapGetters('session', ['customer']),
    filteredCountries() {
      if (!this.countrySearch) {
        return this.countries;
      }
      return this.countries.filter(country =>
        country.name.toLowerCase().includes(this.countrySearch.toLowerCase()) ||
        country.code.toLowerCase().includes(this.countrySearch.toLowerCase())
      );
    },
    howMetOptions() {
      return [
        { value: 'google', text: 'Google' },
        { value: 'facebook', text: 'Facebook' },
        { value: 'instagram', text: 'Instagram' },
        { value: 'recommendation', text: this.$t('words.recommendation') },
        { value: 'website', text: this.$t('words.website') },
        { value: 'other', text: this.$t('words.other') }
      ];
    }
  },
  methods: {
    ...mapActions('basket', ['checkout','setCustomer']),
    ...mapActions('session', ['login','updateCustomerData']),
    goBack() {
      this.$emit('back')
    },
    getLicense() {
      return window._pg?.license || process.env.VUE_APP_LICENSE;
    },
    onInvalid(ev) {
      ev.target.classList.add('touched')
    },
    async update(){
      /* merda per no duplicar clicks al boto pagar */
      if (this.disableSaveButton) {
        return false
      }
      this.disableSaveButton = true
      setTimeout(() => {
        this.disableSaveButton = false
      }, 3000);
      /* final de merda  */
      try {
        let response = await CustomerRepository.edit(this.customer);
        this.updateCustomerData(response.data);
        this.$emit("updated");
      } catch (err) {
        console.log(err);
        await Swal.fire({
          title: i18n.t('messages.sentEmailRecoveryFail'),
          text: i18n.t('messages.sentEmailRecoveryFailText'),
          icon: 'error',
          showCancelButton: false
        });
      }
    },

    // Métodos para manejar el dropdown de países
    selectCountry(country) {
      this.customer.statsCountryCode = country.code;
      this.selectedCountryName = `${country.name} (${country.code})`;
      this.showCountryDropdown = false;
      this.countrySearch = '';
    },

    hideCountryDropdown() {
      setTimeout(() => {
        this.showCountryDropdown = false;
      }, 200);
    },

    keepCountryDropdownOpen() {
      this.showCountryDropdown = true;
    },

    hideCountryDropdownDelayed() {
      setTimeout(() => {
        this.showCountryDropdown = false;
      }, 200);
    },

    // Métodos para manejar el dropdown de "cómo nos conociste"
    selectHowMet(value, text) {
      this.customer.statsHowMet = value;
      this.selectedHowMetName = text;
      this.showHowMetDropdown = false;

      if (value !== 'other') {
        this.customer.statsHowMetOther = '';
      }
    },

    hideHowMetDropdown() {
      setTimeout(() => {
        this.showHowMetDropdown = false;
      }, 200);
    },

    // Inicializar los valores seleccionados basándose en los datos del cliente
    async initializeStatisticsFields() {
      // Verificar que el cliente existe antes de intentar acceder a sus propiedades
      if (!this.customer) {
        return;
      }

      // Si el cliente ya tiene un país seleccionado, usarlo
      if (this.customer.statsCountryCode) {
        const country = this.countries.find(c => c.code === this.customer.statsCountryCode);
        if (country) {
          this.selectedCountryName = `${country.name} (${country.code})`;
        }
      } else {
        // Si no tiene país seleccionado, detectar automáticamente
        try {
          const detectedCountryCode = await getCountryFromGeolocation();
          const detectedCountry = this.countries.find(c => c.code === detectedCountryCode);

          if (detectedCountry) {
            this.customer.statsCountryCode = detectedCountry.code;
            this.selectedCountryName = `${detectedCountry.name} (${detectedCountry.code})`;
          }
        } catch (error) {
          console.warn('Error detecting country:', error);
          // Fallback a España si hay error
          const fallbackCountry = this.countries.find(c => c.code === 'ES');
          if (fallbackCountry) {
            this.customer.statsCountryCode = fallbackCountry.code;
            this.selectedCountryName = `${fallbackCountry.name} (${fallbackCountry.code})`;
          }
        }
      }

      if (this.customer.statsHowMet) {
        const howMetOption = this.howMetOptions.find(option => option.value === this.customer.statsHowMet);
        if (howMetOption) {
          this.selectedHowMetName = howMetOption.text;
        }
      }
    }
  },

  async mounted() {
    await this.initializeStatisticsFields();
  },

  watch: {
    customer: {
      handler() {
        this.initializeStatisticsFields();
      },
      deep: true
    }
  }
}
</script>
<style scoped>
button[type="button"],
button[type="submit"],
input[type=checkbox] {
  cursor: pointer;
}

table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #dedede;
  border-right: none;
  border-bottom: none;
  border-radius: 5px;
  color: #464958;
  margin: auto auto 15px;
}

thead {
  background-color: #fafafa;
}

th,
td {
  border: 1px solid #dedede;
  border-left: none;
  border-top: none;
  padding: 5px;
  vertical-align: middle;
}

.wdgt-product-image {
  width: 70px;
  text-align: center;
}

.wdgt-product {
  width: 60%;
  text-align: left;
}

.wdgt-quantity {
  text-align: center;
}

.wdgt-price {
  text-align: right;
  white-space: nowrap;
}

#_pg-app input.wdgt-quantity {
  -moz-appearance: textfield;
  appearance: textfield;
  margin-bottom: 0;
  display: inline;
  width: 50px;
}

input.wdgt-quantity::-webkit-inner-spin-button,
input.wdgt-quantity::-webkit-outer-spin-button {
  -webkit-appearance: none;
}

input.touched:valid {
  /*
  border: inherit !important;
  outline: inherit !important;
  */
}

input.touched:invalid {
  border: 1px solid rgb(156, 33, 25) !important;
  outline: 1px solid rgb(156, 33, 25) !important;
  box-shadow: 0 0 0 0 rgba(156, 33, 25, 1);
  transform: scale(1);
  animation: pulse 2s 50ms;
}

/* Pulse effect on error */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(156, 33, 25, 0.8);
  }

  50% {
    transform: scale(0.95);
    box-shadow: 0 0 0 10px rgba(156, 33, 25, 0);
  }

  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(156, 33, 25, 0);
  }
}

@media (max-width: 425px) {
  .wdgt-product {
    width: 50%;
  }

  th,
  td {
    padding: 2px;
  }

  td.quantity button {
    display: none;
  }
}

.wdgt-form {
  display: flex;
  flex-wrap: wrap;
}

.form-field {
  margin-bottom: 15px;
  width: 50%;
  padding-right: 15px;
}

.form-field > label {
  display: block;
  margin-bottom: 5px;
}

.form-field.checkbox > label {
  display: inline;
  margin-left: 5px;
}

.checkout-buttons {
  display: flex;
  align-items: center;
}

@media (max-width: 768px) {
  .form-field {
    width: 100%;
  }

  .checkout-buttons {
    flex-direction: column;
    justify-items: center;
    margin-top: 10px;
  }

  .checkout-buttons button {
    width: 100%;
    margin: 0 0 15px 0;
  }
}

@media (max-width: 425px) {
  input {
    width: 100%;
    padding: 5px;
  }
}
.wdgt-price .has-discount{
  text-decoration:line-through;
  color:#333 !important;
  font-weight: normal !important;
}
.wdgt-price-discount{
  color: #a81010;
  white-space: nowrap;
}

button.basket-button,
button.updateButton{
  background-color: #c91919;
  color: white;
  font-size: 20px;
  padding: 10px 40px 10px 40px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
button.checkout{
  background: #acacac;
  color: white;
  font-size: 20px;
  padding: 10px 40px 10px 40px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 1rem;
}
.wdgt-quantity button,
.wdgt-quantity input{
  border: 1px solid #dedede;
  border-radius: 5px;
  padding: 5px 10px;
}
input{
  font-size: 18px;
  padding: 10px;
  margin: 0 0;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #dedede;
  width: 100%;
}
.checkbox input{
  width: auto;
}
.checkbox label {
  line-height: 25px;
}
.wdgt-form .checkbox label:before{
  background-color: #fafafa;
  border: 1px solid #dedede;
  border-radius: 4px;
  top: 0;
}
/* Small devices (portrait tablets and large phones, 600px and up) */
@media only screen and (max-width: 768px) {
  .wdgt-quantity button{
    display: none;
  }
  .payment-buttons button,
  .checkout-buttons button{
    width: 100% !important;
    max-width: 100% !important;
    align-items: center;
    justify-content: center;
  }
  .payment-buttons{
    display: block !important;
  }
  .updateButton{
    margin-top: 1rem !important;
  }
  button.checkout{
    margin: 0;
  }
}

.form-field span.error {
  display: block;
  margin-bottom: 0.5em;
  font-size: .75em;
  color: #f22435;
}
button:disabled{
  opacity: 0.6;
  cursor: not-allowed;
}

/* Country selector styles */
.country-selector {
  position: relative;
  width: 100%;
}

.country-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dedede;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  z-index: 1000;
  max-height: 300px;
  overflow: hidden;
}

.country-search {
  width: 100%;
  padding: 10px;
  border: none;
  border-bottom: 1px solid #dedede;
  font-size: 16px;
  outline: none;
}

.country-list {
  max-height: 250px;
  overflow-y: auto;
}

.country-option {
  padding: 10px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  font-size: 16px;
}

.country-option:hover {
  background-color: #f5f5f5;
}

.country-option:last-child {
  border-bottom: none;
}

/* How met selector styles */
.how-met-selector {
  position: relative;
  width: 100%;
}

.how-met-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dedede;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  z-index: 1000;
  max-height: 300px;
  overflow: hidden;
}

.how-met-list {
  max-height: 250px;
  overflow-y: auto;
}

.how-met-option {
  padding: 10px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  font-size: 16px;
}

.how-met-option:hover {
  background-color: #f5f5f5;
}

.how-met-option:last-child {
  border-bottom: none;
}

/* Form section title styles */
.form-section-title {
  width: 100%;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 2px solid #dedede;
}

.form-section-title h3 {
  margin: 0;
  font-size: 18px;
  color: #464958;
  font-weight: 600;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .country-dropdown,
  .how-met-dropdown {
    max-height: 200px;
  }

  .country-list,
  .how-met-list {
    max-height: 150px;
  }

  .country-option,
  .how-met-option {
    padding: 8px;
    font-size: 14px;
  }

  .form-section-title h3 {
    font-size: 16px;
  }
}

textarea {
  font-size: 18px;
  padding: 10px;
  margin: 0 0;
  border: none;
  border-radius: 4px;
  border: 1px solid #dedede;
  width: 100%;
  resize: vertical;
  font-family: inherit;
  min-height: 60px;
}
</style>
